import { Listbox } from '@headlessui/react';
import { GlobeAltIcon } from '@heroicons/react/solid';

import Cookies from 'js-cookie';
import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';

import clsxm from '@/lib/clsxm';

import { SimpleTooltip } from '@/components/ui/tooltip';

export const languageOptions = [
  { name: 'English', value: 'en' },
  { name: '日本語', value: 'ja' },
  { name: 'Español', value: 'es' },
  { name: 'Français', value: 'fr' },
  { name: 'Italiano', value: 'it' },
  { name: 'Deutsch', value: 'de' },
  { name: 'Korean', value: 'ko' },
  { name: '简体中文', value: 'zh' },
  { name: 'ไทย', value: 'th' },
  { name: 'العربية', value: 'ar' },
  { name: 'Português', value: 'pt' },
  { name: 'Bahasa Melayu', value: 'ms' },
  { name: 'Tiếng Việt', value: 'vi' },
] as const;

interface LanguageSelectorProps {
  variant?: 'default' | 'light';
  align?: 'left' | 'right' | 'center';
  className?: string;
}

export function LanguageSelector({
  variant = 'default',
  align = 'center',
  className,
  ...props
}: LanguageSelectorProps) {
  const { t, lang } = useLocale('common');
  const router = useRouter();

  const handleLocaleChange = (
    selectedLanguage: (typeof languageOptions)[number],
  ) => {
    // Set the NEXT_LOCALE cookie with the selected locale
    Cookies.set('NEXT_LOCALE', selectedLanguage.value, { expires: 365 });

    // Redirect to the current page with the new locale
    router.push(router.asPath, router.asPath, {
      locale: selectedLanguage.value,
    });
  };

  const buttonClasses =
    variant === 'light'
      ? 'flex h-6 w-6 items-center justify-center rounded-lg bg-white/10 backdrop-blur-sm ring-1 ring-white/20 hover:bg-white/20'
      : 'flex h-6 w-6 items-center justify-center rounded-lg shadow-md shadow-black/5 ring-1 ring-black/5 dark:bg-slate-700 dark:ring-inset dark:ring-white/5';

  const iconClasses =
    variant === 'light' ? 'h-4 w-4 fill-white' : 'h-4 w-4 fill-slate-400';

  return (
    <Listbox
      as='div'
      value={
        languageOptions.find((option) => option.value === lang) ??
        languageOptions[0]
      }
      onChange={handleLocaleChange}
      className={className}
      {...props}
    >
      <Listbox.Label className='sr-only'>{t('Language')}</Listbox.Label>
      <SimpleTooltip title={t('Language')} side='bottom'>
        <Listbox.Button className={buttonClasses}>
          <GlobeAltIcon className={iconClasses} />
        </Listbox.Button>
      </SimpleTooltip>
      <Listbox.Options
        className={clsxm(
          'absolute top-full z-50 mt-3 w-36 space-y-1 rounded-xl bg-white p-3 text-sm font-medium shadow-md shadow-black/5 ring-1 ring-black/5 dark:bg-slate-800 dark:ring-white/5',
          align === 'center' && 'left-1/2 -translate-x-1/2',
          align === 'left' && 'left-0',
          align === 'right' && 'right-0',
        )}
      >
        {languageOptions.map((language) => (
          <Listbox.Option
            key={language.value}
            value={language}
            className={({ active }) =>
              clsxm(
                'flex cursor-pointer select-none items-center rounded-[0.625rem] p-1',
                {
                  'text-primary-500': lang === language.value,
                  'text-slate-900 dark:text-white':
                    active && lang !== language.value,
                  'text-slate-700 dark:text-slate-400':
                    !active && lang !== language.value,
                  'bg-slate-100 dark:bg-slate-900/40': active,
                },
              )
            }
          >
            {() => (
              <>
                <div className='ml-3'>{t(language.name)}</div>
              </>
            )}
          </Listbox.Option>
        ))}
      </Listbox.Options>
    </Listbox>
  );
}
